import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// Request interceptor - add JWT token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  verify: () => api.get('/auth/verify'),
};

// Dashboard
export const dashboardAPI = {
  get: () => api.get('/dashboard'),
};

// Calls
export const callsAPI = {
  getRecordingUrl: (callId) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/calls/${callId}/recording?token=${encodeURIComponent(token || '')}`;
  },
  downloadRecording: (callId) => {
    const token = localStorage.getItem('token');
    return `${API_BASE}/calls/${callId}/download?token=${encodeURIComponent(token || '')}`;
  },
  list: (params) => api.get('/calls', { params }),
  stats: (params) => api.get('/calls/stats', { params }),
  
  
  updateNotes: (callId, notes) => api.put(`/calls/${callId}/notes`, { notes }),
};

// Operators
export const operatorsAPI = {
  list: () => api.get('/operators'),
  get: (id) => api.get(`/operators/${id}`),
  create: (data) => api.post('/operators', data),
  update: (id, data) => api.put(`/operators/${id}`, data),
  delete: (id) => api.delete(`/operators/${id}`),
  productivity: (params = {}) => api.get('/operators/productivity', { params }),
};

// Status
export const statusAPI = {
  update: (status) => api.put('/status', { status }),
  getAll: () => api.get('/status/operators'),
  availableOperators: () => api.get('/status/available-operators'),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (settings) => api.put('/settings', { settings }),
};





export const contactsAPI = {
  list: (params = {}) => api.get('/contacts', { params }),
  getByPhone: (phone) => api.get(`/contacts/by-phone/${encodeURIComponent(phone)}`),
  get: (id) => api.get(`/contacts/${id}`),
  timeline: (id) => api.get(`/contacts/${id}/timeline`),
  addNote: (id, body) => api.post(`/contacts/${id}/note`, { body }),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
};

// CRM: Bitimlar (pipeline)
export const dealsAPI = {
  stages: () => api.get('/deals/stages'),
  stats: () => api.get('/deals/stats'),
  board: (params = {}) => api.get('/deals/board', { params }),
  list: (params = {}) => api.get('/deals', { params }),
  get: (id) => api.get(`/deals/${id}`),
  create: (data) => api.post('/deals', data),
  update: (id, data) => api.put(`/deals/${id}`, data),
  moveStage: (id, stage_id) => api.patch(`/deals/${id}/stage`, { stage_id }),
  delete: (id) => api.delete(`/deals/${id}`),
};

// CRM: Natija kodlari (disposition)
export const dispositionsAPI = {
  codes: () => api.get('/dispositions'),
  stats: (params = {}) => api.get('/dispositions/stats', { params }),
  exportOutcomes: (params = {}) => api.get('/dispositions/export', { params, responseType: 'blob' }),
  setOutcome: (data) => api.post('/dispositions/outcome', data),
  create: (data) => api.post('/dispositions', data),
  update: (id, data) => api.put(`/dispositions/${id}`, data),
  delete: (id) => api.delete(`/dispositions/${id}`),
};

// CRM: Vazifalar
export const tasksAPI = {
  summary: () => api.get('/tasks/summary'),
  list: (params = {}) => api.get('/tasks', { params }),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  complete: (id) => api.patch(`/tasks/${id}/complete`),
  delete: (id) => api.delete(`/tasks/${id}`),
};


// Bilim bazasi / skriptlar
export const knowledgeAPI = {
  list: (params = {}) => api.get('/knowledge', { params }),
  categories: () => api.get('/knowledge/categories'),
  get: (id) => api.get(`/knowledge/${id}`),
  create: (data) => api.post('/knowledge', data),
  update: (id, data) => api.put(`/knowledge/${id}`, data),
  delete: (id) => api.delete(`/knowledge/${id}`),
};

export const businessHoursAPI = {
  get: () => api.get('/business-hours'),
  isOpen: () => api.get('/business-hours/is-open'),
  updateDay: (day, data) => api.put(`/business-hours/day/${day}`, data),
  updateSettings: (data) => api.put('/business-hours/settings', data),
  addHoliday: (data) => api.post('/business-hours/holidays', data),
  deleteHoliday: (id) => api.delete(`/business-hours/holidays/${id}`),
};

export const forwardingAPI = {
  list: () => api.get('/forwarding'),
  status: () => api.get('/forwarding/status'),
  create: (data) => api.post('/forwarding', data),
  update: (id, data) => api.put(`/forwarding/${id}`, data),
  delete: (id) => api.delete(`/forwarding/${id}`),
};

export const adminsAPI = {
  list: () => api.get('/admins'),
  create: (data) => api.post('/admins', data),
  update: (id, data) => api.put(`/admins/${id}`, data),
  delete: (id) => api.delete(`/admins/${id}`),
  changePassword: (data) => api.put('/admins/me/password', data),
  auditLog: () => api.get('/admins/audit/recent'),
};

// Qo'ng'iroq audio diagnostikasi (tarmoqmi yoki mikrofonmi)
export const audioDiagnosticsAPI = {
  list: (params = {}) => api.get('/audio-diagnostics', { params }),
  summary: (params = {}) => api.get('/audio-diagnostics/summary', { params }),
};

// Izohlar tahlili (admin panel → "Izohlar tahlili")
export const noteAnalyticsAPI = {
  get: (params = {}) => api.get('/note-analytics', { params }),
};

// Tashkilotlar va filiallar ma'lumotnomasi (AuditApteka asosiy bazasidan)
export const orgDirectoryAPI = {
  organizations: (params = {}) => api.get('/org-directory/organizations', { params }),
  branches: (ref) => api.get('/org-directory/branches', { params: { ref } }),
  // Tashkilot (va filial) qarzi — qo'ng'iroq paytida operatorga ko'rsatiladi
  debt: (ref, branch) => api.get('/org-directory/debt', { params: { ref, ...(branch ? { branch } : {}) } }),
};

export default api;
